/* Joining a team — STORY-007, and the route STORY-004's chooser was waiting on.
 *
 * Two ways in, and the screen behaves differently for each on purpose. A link
 * from the invitation email carries a token and needs no interaction at all —
 * asking somebody to confirm something they have already clicked is a step that
 * exists only to be got wrong. A code was told to somebody verbally, so it is
 * typed.
 *
 * The code is sent as typed. Folding it — O for zero, I and L for one, case,
 * dashes — happens once, on the server, so the two cannot come to disagree
 * about what a code means.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { AuthScreen, Button, Callout, TextField } from '../../../shared/ui/index.js';
import { useAcceptInvitation } from '../api/index.js';

export function AcceptInvitationScreen() {
  const t = useTranslate();
  const navigate = useNavigate();
  const { token } = useSearch({ strict: false }) as { token?: string };

  const accept = useAcceptInvitation();
  const [code, setCode] = useState('');

  /* Stable across renders. The effect below depends on it, and a fresh
   * identity each render is exactly the thing that would re-run a one-shot
   * effect — the ref catches it, but a dependency that changes every render is
   * a dependency that documents nothing. */
  const enter = useCallback(
    (organisationId: string) => {
      void navigate({
        to: '/orgs/$organisationId/calendar',
        params: { organisationId },
      });
    },
    [navigate],
  );

  /* A token in the URL is spent once, on arrival. The ref is what stops a
   * re-render spending it again — the second attempt would answer "no longer
   * valid" about an invitation that had just worked, which reads as the join
   * having failed. */
  const attempted = useRef(false);
  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    accept.mutate({ token }, { onSuccess: (joined) => enter(joined.organisationId) });
  }, [token, accept, enter]);

  if (token) {
    return (
      <AuthScreen title={t('accept.title')} brand={t('app.name')}>
        {accept.isError ? <AcceptError error={accept.error} /> : null}
        {accept.isError ? null : <p className="field__hint">{t('accept.joining')}</p>}
      </AuthScreen>
    );
  }

  return (
    <AuthScreen title={t('accept.title')} brand={t('app.name')} subtitle={t('accept.body')}>
      {accept.isError ? <AcceptError error={accept.error} /> : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          accept.mutate({ code }, { onSuccess: (joined) => enter(joined.organisationId) });
        }}
        noValidate
      >
        <TextField
          label={t('accept.code_label')}
          hint={t('accept.code_hint')}
          autoComplete="off"
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        <Button type="submit" block busy={accept.isPending}>
          {accept.isPending ? t('common.submitting') : t('accept.submit')}
        </Button>
      </form>
    </AuthScreen>
  );
}

function AcceptError({ error }: { readonly error: unknown }) {
  const t = useTranslate();

  /* Branching on the CODE, never the message. And one message for every way an
   * invitation can be dead: the server refuses to tell expired from used from
   * withdrawn from never-existed from issued-to-somebody-else, and a client
   * that appeared to would be inventing a distinction it does not have. */
  if (error instanceof ApiError && error.code === 'not_found') {
    return <Callout tone="warning">{t('accept.invalid')}</Callout>;
  }
  if (error instanceof ApiError && error.code === 'rate_limited') {
    return <Callout tone="warning">{t('error.rate_limited')}</Callout>;
  }
  if (error instanceof NetworkError) {
    return <Callout tone="danger">{t('error.network')}</Callout>;
  }
  return <Callout tone="danger">{t('error.unexpected')}</Callout>;
}
